/**
 * CapEx — the capital-expenditure register for one entity: every investment with its budget,
 * actual cost, dates, status and the expected return computed by the API from the owner's
 * estimated revenue impact. Phase 5, FINANCE.md §14.
 *
 * Every figure comes from `GET /api/capex`; this screen adds nothing up. The form records an
 * item from what the owner knows when it is planned — dollars typed as text and converted
 * without a float — and the row's controls progress it: in progress, complete (with the actual
 * cost and the date), cancelled. Owner only for every write; the API enforces it.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dollarsToCents } from '@sixgen/core';
import { api, type CapexResponse, type Property } from '../../api.js';
import { EntityPicker, useEntities, useEntityId } from '../../components/EntityPicker.js';
import { formatMoney } from '../../money.js';

const STATUS_LABEL: Record<string, string> = {
  planned: 'Planned',
  in_progress: 'In progress',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

export function CapExPage(): ReactNode {
  const entityId = useEntityId();
  const entities = useEntities();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [stored, setStored] = useState<string | null>(null);
  const [completing, setCompleting] = useState<string | null>(null);

  const items = useQuery({
    queryKey: ['capex', entityId],
    enabled: entityId !== null,
    queryFn: () => api.get<CapexResponse>(`/api/capex?entity=${entityId}`),
  });
  const properties = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get<{ properties: Property[] }>('/api/properties'),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['capex'] });
    void queryClient.invalidateQueries({ queryKey: ['finance', 'cash'] });
  };
  const create = useMutation({
    mutationFn: (body: unknown) => api.post<{ item: { name: string } }>('/api/capex', body),
    onSuccess: (result) => {
      setError(null);
      setStored(`Recorded ${result.item.name}.`);
      refresh();
    },
    onError: (err: unknown) => {
      setStored(null);
      setError(err instanceof Error ? err.message : String(err));
    },
  });
  const update = useMutation({
    mutationFn: (input: { id: string; body: unknown }) =>
      api.patch<{ item: { name: string; status: string } }>(`/api/capex/${input.id}`, input.body),
    onSuccess: (result) => {
      setError(null);
      setStored(`${result.item.name}: ${STATUS_LABEL[result.item.status] ?? result.item.status}.`);
      setCompleting(null);
      refresh();
    },
    onError: (err: unknown) => {
      setStored(null);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  function submit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!entityId) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const text = (name: string): string => String(data.get(name) ?? '').trim();
    try {
      const impact = text('impact');
      const property = text('property');
      create.mutate({
        entityId,
        propertyId: property === '' ? null : property,
        name: text('name'),
        vendor: text('vendor') === '' ? null : text('vendor'),
        plannedOn: text('plannedOn'),
        budgetCents: dollarsToCents(text('budget')).toString(),
        estimatedAnnualRevenueImpactCents: impact === '' ? null : dollarsToCents(impact).toString(),
        notes: text('notes') === '' ? null : text('notes'),
      });
      form.reset();
    } catch (err) {
      setStored(null);
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  function complete(event: React.FormEvent<HTMLFormElement>, id: string): void {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      update.mutate({
        id,
        body: {
          status: 'complete',
          actualCostCents: dollarsToCents(String(data.get('actual') ?? '')).toString(),
          completedOn: String(data.get('completedOn') ?? ''),
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const noEntities = entities.data !== undefined && entities.data.entities.length === 0;
  const data = items.data;
  const entityProperties = (properties.data?.properties ?? []).filter(
    (p) => p.entity_id === entityId,
  );
  const propertyName = (id: string | null): string =>
    id === null ? '—' : (properties.data?.properties.find((p) => p.id === id)?.name ?? id);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>CapEx</h1>
          <p className="subtitle">
            Every investment with what it was meant to cost, what it did, and the return expected of
            it. Planned items are outflows in the cash forecast until they complete.
          </p>
        </div>
      </header>

      <div className="filters">
        <EntityPicker />
      </div>

      {(items.isLoading || (entityId === null && entities.isLoading)) && (
        <p className="muted">Loading…</p>
      )}
      {items.isError && <p className="error">Could not load CapEx: {items.error.message}</p>}
      {noEntities && (
        <div className="card empty">
          <h2>No entities.</h2>
          <p>
            Run <code>pnpm seed</code> to load the fictional sample portfolio.
          </p>
        </div>
      )}

      {data && data.items.length === 0 && (
        <div className="card empty">
          <h2>No CapEx recorded for this entity.</h2>
          <p>Record an investment below: what, where, when, and what it should cost.</p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Investment</th>
                <th>Property</th>
                <th>Planned</th>
                <th className="num">Budget</th>
                <th className="num">Actual</th>
                <th className="num">Est. annual impact</th>
                <th className="num">Expected return</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(({ item, expected_roi, cost_is_actual }) => (
                <tr key={item.id} className={item.status === 'cancelled' ? 'muted' : ''}>
                  <td>
                    {item.name}
                    {item.vendor && <span className="muted small"> · {item.vendor}</span>}
                  </td>
                  <td className="muted">{propertyName(item.property_id)}</td>
                  <td>{item.planned_on}</td>
                  <td className="num">{formatMoney(item.budget_cents)}</td>
                  <td className="num">
                    {item.actual_cost_cents === null ? (
                      <span className="muted">—</span>
                    ) : (
                      formatMoney(item.actual_cost_cents)
                    )}
                  </td>
                  <td className="num">
                    {item.estimated_annual_revenue_impact_cents === null ? (
                      <span className="muted">—</span>
                    ) : (
                      formatMoney(item.estimated_annual_revenue_impact_cents)
                    )}
                  </td>
                  <td
                    className="num"
                    title={cost_is_actual ? 'over the actual cost' : 'over the budget'}
                  >
                    {expected_roi === null ? '—' : `${(expected_roi * 100).toFixed(0)}%`}
                  </td>
                  <td>
                    {STATUS_LABEL[item.status] ?? item.status}
                    {item.completed_on && <span className="muted small"> {item.completed_on}</span>}
                    {(item.status === 'planned' || item.status === 'in_progress') && (
                      <div className="inline-actions">
                        {item.status === 'planned' && (
                          <button
                            type="button"
                            className="link-button"
                            onClick={() =>
                              update.mutate({ id: item.id, body: { status: 'in_progress' } })
                            }
                          >
                            Start
                          </button>
                        )}
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => setCompleting(completing === item.id ? null : item.id)}
                        >
                          Complete…
                        </button>
                        <button
                          type="button"
                          className="link-button"
                          onClick={() =>
                            update.mutate({ id: item.id, body: { status: 'cancelled' } })
                          }
                        >
                          Cancel
                        </button>
                        {completing === item.id && (
                          <form className="inline-form" onSubmit={(e) => complete(e, item.id)}>
                            <input
                              name="actual"
                              required
                              inputMode="decimal"
                              placeholder="Actual $"
                              aria-label="Actual cost"
                            />
                            <input
                              name="completedOn"
                              required
                              type="date"
                              aria-label="Completed on"
                            />
                            <button type="submit" disabled={update.isPending}>
                              Save
                            </button>
                          </form>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {entityId && (
        <section className="card">
          <h2>Record an investment</h2>
          <form className="stack" onSubmit={submit}>
            <div className="field-row">
              <label>
                Investment
                <input name="name" required maxLength={200} placeholder="Hot tub, Sketch Rd" />
              </label>
              <label>
                Vendor
                <input name="vendor" maxLength={200} />
              </label>
              <label>
                Property
                <select name="property" defaultValue="">
                  <option value="">— none / entity-level —</option>
                  {entityProperties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="field-row">
              <label>
                Planned for
                <input name="plannedOn" required type="date" />
              </label>
              <label>
                Budget ($)
                <input name="budget" required inputMode="decimal" placeholder="12,500.00" />
              </label>
              <label>
                Est. annual revenue impact ($)
                <input name="impact" inputMode="decimal" placeholder="optional" />
              </label>
            </div>
            <label>
              Notes
              <input name="notes" maxLength={2000} />
            </label>
            <div>
              <button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Recording…' : 'Record investment'}
              </button>
            </div>
          </form>
          {stored && <p className="muted">{stored}</p>}
          {error && <p className="error">{error}</p>}
        </section>
      )}
    </div>
  );
}
